import { lazy } from 'react'

import { EngagementTabsDocument, type EngagementTabsQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { type NavigationRoutesFn, buildModuleRoute } from 'modules/core/lib/react-router'

import { engagementInsightsRoutes } from 'modules/engagement_insights/routes'
import smartSurveysRoutes from 'modules/engagement_surveys/routes'
import enpsRoutes from 'modules/enps/routes'
import { oneOnOnesRoutes } from 'modules/meetings/routes/oneOnOnesRoutes'
import surveysRoutes from 'modules/surveys/routes'

const RedirectToRoutePage = lazy(() => import('modules/engagement/pages/RedirectToRoutePage'))
const RootPage = lazy(() => import('modules/engagement/pages/RootPage'))
const PulseRootPage = lazy(() => import('modules/engagement/pages/pulse/RootPage'))
const RedirectPulse = lazy(() => import('modules/engagement/pages/RedirectPulse'))
const PulsePage = lazy(() => import('modules/engagement/pages/pulse'))
const AiReportPage = lazy(() => import('modules/engagement/pages/pulseAiReport'))

const featureEnabled = (data: EngagementTabsQuery, featureName: string) => {
  return (
    data.features.featuresConnection?.edges.find((feature) => feature.node.name === featureName)
      ?.node.enabled ?? false
  )
}

export const engagementRoutes: NavigationRoutesFn = (navigation) => {
  return [
    buildModuleRoute({
      path: navigation.engagement.path,
      element: <RootPage />,
      handle: {
        module: 'engagement',
        moduleName: () => i18n.t('sidebar.engagement'),
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: EngagementTabsDocument,
            variables: {
              companyId: session.company.id,
            },
          }).map(({ data }) => {
            const settingsItems: { label: string; href: string }[] = []
            const settingsManage = data?.engagement?._policies.settingsManage.can

            if (
              data?.surveys?._policies.sidebarSeeEngagementSurveysSubLevelItem.can === true &&
              data?.apiCore?._policies.accessSurveysCreateTemplates.can === true
            ) {
              settingsItems.push({
                label: i18n.t('surveys.index.title'),
                href: navigation.settings.surveys.fullPath(),
              })
            }

            const pulseFeature = featureEnabled(data, 'engagement_employee_pulse_questions')
            const meetingsVisible = data?.meetings?._policies.seeMeetings.can

            if ((pulseFeature || meetingsVisible) && settingsManage) {
              settingsItems.push({
                label: i18n.t('engagement.settings.title'),
                href: navigation.settings.engagement.fullPath(),
              })
            }

            const upsellFlowEnabled = data.upsell._policies.canRequestUpsell.can ?? false

            const surveysVisible =
              data?.surveys?._policies.sidebarSeeEngagementSurveysSubLevelItem.can
            const enpsVisible = data?.enps?._policies.sidebarSeeEngagementEnpsSubLevelItem.can
            const insightsVisible =
              data?.engagementInsights?._policies.sidebarSeeEngagementInsightsSubLevelItem.can
            const smartSurveysVisible =
              data?.surveys?._policies.sidebarSeeSmartSurveysSubLevelItem.can

            return {
              title: i18n.t('sidebar.engagement'),
              settings: settingsItems.length > 0 ? { items: settingsItems } : undefined,
              subNavigation: [
                {
                  id: 'navigation.engagement.one_on_ones.fullPath',
                  title: i18n.t('engagement.one_on_ones.tab_title'),
                  href: navigation.engagement.one_on_ones.fullPath(),
                  isVisible: meetingsVisible,
                },
                {
                  id: 'navigation.engagement.pulse.fullPath',
                  title: i18n.t('engagement.pulse.title'),
                  href: navigation.engagement.pulse.fullPath(),
                  isVisible: data?.engagement?._policies.seePulseDashboard.can,
                },
                {
                  id: 'navigation.engagement.surveys.fullPath',
                  title: i18n.t('surveys.index.title'),
                  href: navigation.engagement.surveys.fullPath(),
                  isVisible: upsellFlowEnabled || surveysVisible,
                  variant: upsellFlowEnabled && !surveysVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.smart_surveys.fullPath',
                  title: i18n.t('surveys.smart_surveys'),
                  href: navigation.engagement.smart_surveys.fullPath(),
                  isVisible: smartSurveysVisible,
                  variant: upsellFlowEnabled && !smartSurveysVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.enps.fullPath',
                  title: i18n.t('enps.index.title'),
                  href: navigation.engagement.enps.fullPath(),
                  isVisible: enpsVisible || upsellFlowEnabled,
                  variant: upsellFlowEnabled && !enpsVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.insights.fullPath',
                  title: i18n.t('surveys.insights'),
                  href: navigation.engagement.insights.fullPath(),
                  index: true,
                  isVisible: insightsVisible || upsellFlowEnabled,
                  variant: upsellFlowEnabled && !insightsVisible ? 'upsell' : undefined,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToRoutePage />,
        },
        ...engagementInsightsRoutes(navigation),
        {
          path: navigation.engagement.pulse.path,
          element: <PulseRootPage />,
          handle: {
            meta: ({ watchQuery, session, navigate }) => {
              return watchQuery({
                query: EngagementTabsDocument,
                variables: {
                  companyId: session.company.id,
                },
              }).map(({ data }) => {
                if (!data.engagement._policies.seePulseDashboard.can) {
                  navigate(navigation.engagement.fullPath())
                }

                const pulseAiReportFeature = featureEnabled(
                  data,
                  'engagement_employee_pulse_ai_report'
                )

                const subNavigation = pulseAiReportFeature
                  ? [
                      {
                        id: 'navigation.engagement.pulse.participation.fullPath',
                        title: i18n.t('engagement.pulse.participation.title'),
                        href: navigation.engagement.pulse.participation.fullPath(),
                      },
                      {
                        id: 'navigation.engagement.pulse.aiReport.fullPath',
                        title: i18n.t('engagement.pulse.pulse_ai_report.title'),
                        href: navigation.engagement.pulse.aiReport.fullPath(),
                        isVisible: pulseAiReportFeature,
                      },
                    ]
                  : []

                return {
                  title: i18n.t('engagement.pulse.title'),
                  subNavigation,
                }
              })
            },
          },
          children: [
            { path: '', element: <RedirectPulse />, index: true },
            { path: navigation.engagement.pulse.participation.path, element: <PulsePage /> },
            {
              path: navigation.engagement.pulse.aiReport.path,
              element: <AiReportPage />,
              handle: {
                meta: ({ watchQuery, session, navigate }) => {
                  return watchQuery({
                    query: EngagementTabsDocument,
                    variables: {
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    if (!featureEnabled(data, 'engagement_employee_pulse_ai_report')) {
                      navigate(navigation.engagement.pulse.participation.fullPath())
                    }

                    return {
                      title: i18n.t('engagement.pulse.pulse_ai_report.title'),
                    }
                  })
                },
              },
            },
          ],
        },
        ...surveysRoutes(navigation),
        ...smartSurveysRoutes(navigation),
        ...enpsRoutes(navigation),
        ...oneOnOnesRoutes(navigation),
      ],
    }),
  ]
}
